/**
 * homeContent.ts
 *
 * Загрузка данных главной страницы из home_page_content через admin-db-proxy.
 * Использует тот же механизм, что и CMS (cmsBlocksDb через adminDb.ts).
 * Если данных нет — возвращаются статические fallback-значения.
 */

import { homePageContentDb } from './adminDb';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface HomeAboutBlock {
  title: string;
  description: string;
  stat1Value: string;
  stat1Label: string;
  stat2Value: string;
  stat2Label: string;
  buttonText: string;
  buttonUrl: string;
  imageUrl: string;
  quote: string;
  quoteAuthor: string;
}

export interface HomeCollectionCard {
  id: string;
  image: string;
  name: string;
  description: string;
  url: string;
}

export interface HomeCollectionsBlock {
  title: string;
  subtitle: string;
  cards: HomeCollectionCard[];
}

export interface HomeProjectsBlock {
  title: string;
  subtitle: string;
  buttonText: string;
  buttonUrl: string;
}

export interface HomeDealerBlock {
  title: string;
  description: string;
  buttonText: string;
  buttonUrl: string;
}

export interface HomePageData {
  about: HomeAboutBlock;
  collections: HomeCollectionsBlock;
  projects: HomeProjectsBlock;
  dealer: HomeDealerBlock;
}

// ─── Fallback (static defaults) ────────────────────────────────────────────────

export const HOME_DEFAULTS: HomePageData = {
  about: {
    title: '',
    description: '',
    stat1Value: '',
    stat1Label: '',
    stat2Value: '',
    stat2Label: '',
    buttonText: '',
    buttonUrl: '',
    imageUrl: '',
    quote: '',
    quoteAuthor: '',
  },
  collections: {
    title: '',
    subtitle: '',
    cards: [],
  },
  projects: {
    title: '',
    subtitle: '',
    buttonText: '',
    buttonUrl: '',
  },
  dealer: {
    title: '',
    description: '',
    buttonText: '',
    buttonUrl: '',
  },
};

// ─── Loader ────────────────────────────────────────────────────────────────────

async function loadBlock<T>(blockKey: string, lang: string, fallback: T): Promise<T> {
  // Try lang-specific key first (new format: block__lang)
  const langKey = `${blockKey}__${lang}`;
  try {
    const res = await homePageContentDb.get(langKey);
    if (res.ok && res.data && (res.data as any).contentJson) {
      const parsed = JSON.parse((res.data as any).contentJson);
      if (typeof parsed === 'object' && parsed !== null) {
        return { ...fallback, ...parsed } as T;
      }
    }
  } catch { /* try legacy */ }

  // Fallback: try legacy key without lang suffix (backward compat)
  try {
    const res = await homePageContentDb.get(blockKey);
    if (res.ok && res.data && (res.data as any).contentJson) {
      const parsed = JSON.parse((res.data as any).contentJson);
      if (typeof parsed === 'object' && parsed !== null) {
        return { ...fallback, ...parsed } as T;
      }
    }
  } catch { /* silent */ }

  return fallback;
}

// Module-scope cache for the listAll() response: it returns rows for ALL
// languages, so calling it once per language was wasteful. We keep the rows
// for 5 minutes and share them across language calls.
let _homeBlocksCache: { rows: any[]; at: number } | null = null;
let _homeBlocksInFlight: Promise<any[]> | null = null;
const HOME_BLOCKS_TTL_MS = 5 * 60 * 1000;

async function getAllHomeBlocks(): Promise<any[]> {
  if (_homeBlocksCache && Date.now() - _homeBlocksCache.at < HOME_BLOCKS_TTL_MS) {
    return _homeBlocksCache.rows;
  }
  if (_homeBlocksInFlight) return _homeBlocksInFlight;

  _homeBlocksInFlight = (async () => {
    try {
      const res = await homePageContentDb.listAll();
      const rows = res.ok && Array.isArray(res.data) ? res.data : [];
      _homeBlocksCache = { rows, at: Date.now() };
      return rows;
    } catch {
      return [];
    } finally {
      _homeBlocksInFlight = null;
    }
  })();
  return _homeBlocksInFlight;
}

export async function loadHomePageData(lang = 'ru'): Promise<HomePageData> {
  // Single list request fetches all home_page_content rows in one round-trip,
  // instead of 4 point reads (or 8 in the legacy-fallback worst case).
  // Result is cached at module scope and shared across language calls.
  const blocks = await getAllHomeBlocks();

  const byId = new Map<string, any>();
  for (const b of blocks) {
    if (b && (b as any).id) byId.set((b as any).id, b);
  }

  const pickBlock = <T,>(blockKey: string, fallback: T): T => {
    // Prefer language-suffixed key, fall back to legacy un-suffixed key.
    const candidate = byId.get(`${blockKey}__${lang}`) ?? byId.get(blockKey);
    if (candidate && candidate.contentJson) {
      try {
        const parsed = JSON.parse(candidate.contentJson);
        if (typeof parsed === 'object' && parsed !== null) {
          return { ...fallback, ...parsed } as T;
        }
      } catch { /* keep fallback */ }
    }
    return fallback;
  };

  return {
    about: pickBlock('about_block', HOME_DEFAULTS.about),
    collections: pickBlock('collections_block', HOME_DEFAULTS.collections),
    projects: pickBlock('projects_block', HOME_DEFAULTS.projects),
    dealer: pickBlock('dealer_block', HOME_DEFAULTS.dealer),
  } as unknown as HomePageData;
  // (Legacy code removed — see loadBlock below for backward compat helper.)
}
